// VideoUploader.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../css/VideoUploader.module.css";
import { fetchAuthSession } from "aws-amplify/auth";

// === presigned PUT backend ===
const LAMBDA_URL = "https://2vz2smjmq43gp5hofz2ptfhpry0jiuyt.lambda-url.us-east-2.on.aws/";
const S3_BUCKET = "t4astorage580ed-dev";
const S3_REGION = "us-east-2";

const DEBUG = true;

const isVideoDrag = (e) => {
    const items = e.dataTransfer?.items;
    if (!items) return true; // fallback: allow styling if browser doesn't expose items
    return Array.from(items).some(
        (it) => it.kind === "file" && (it.type || "").startsWith("video/")
    );
};

const safeJSON = (v) => {
    try { return JSON.stringify(v, Object.getOwnPropertyNames(v)); }
    catch { try { return JSON.stringify(v); } catch { return String(v); } }
};

const isMobileUA = () =>
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const getOrigin = () =>
    (typeof window !== "undefined" && window.location ? window.location.origin : "n/a");

const getUA = () =>
    (typeof navigator !== "undefined" ? navigator.userAgent : "n/a");

const DEFAULT_PREFIX = "public/uploads/videos/";

function getNetworkTuning() {
    if (typeof navigator === "undefined") return { uiHint: "normal" };
    const con = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
    let uiHint = "normal";
    if (con?.effectiveType) {
        if (["slow-2g", "2g"].includes(con.effectiveType) || con?.saveData) uiHint = "slow";
        if (con?.effectiveType === "3g") uiHint = "moderate";
    }
    return { uiHint };
}

const fmt = (b) => {
    if (!b && b !== 0) return "-";
    const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 ? 2 : 1)} ${u[i]}`;
};

const formatEta = (sec) => {
    if (sec == null || !isFinite(sec)) return "—";
    const s = Math.max(0, Math.round(sec)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
    if (h > 0) return `${h}h ${m}m ${r}s`;
    if (m > 0) return `${m}m ${r}s`;
    return `${r}s`;
};

const guessMimeFromName = (name = "") => {
    const ext = name.split(".").pop()?.toLowerCase();
    const map = {
        mp4: "video/mp4",
        mov: "video/quicktime",
        webm: "video/webm",
        mkv: "video/x-matroska",
        m4v: "video/x-m4v",
        avi: "video/x-msvideo",
        "3gp": "video/3gpp",
        "3gpp": "video/3gpp",
        ogg: "video/ogg",
        ogv: "video/ogg",
    };
    return map[ext] || null;
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

async function hydrateMobileCloudFile(rawFile, logFn = console.log) {
    const log = (...a) => logFn("[HYDRATE]", ...a);

    if (!rawFile) { log("No file passed in"); return rawFile; }

    log("Starting hydration", { name: rawFile.name, type: rawFile.type, size: rawFile.size });

    if (rawFile.size > 0) {
        log("File already has bytes — skipping hydration");
        return rawFile;
    }

    const guessedType = guessMimeFromName(rawFile.name) || "application/octet-stream";

    for (let i = 0; i < 12; i++) {
        try {
            log(`Attempt 0.${i}: Response(file).arrayBuffer()`);
            const buf = await new Response(rawFile).arrayBuffer();
            if (buf && buf.byteLength > 0) {
                log("Stream read succeeded with", buf.byteLength, "bytes");
                const blob = new Blob([buf], { type: rawFile.type || guessedType });
                const hydrated = new File([blob], rawFile.name || `video_${Date.now()}.mp4`, { type: blob.type });
                log("Returning hydrated file (stream)");
                return hydrated;
            }
        } catch (err) {
            log("Stream read err", err?.name || "", String(err));
        }
        await sleep(Math.min(100 + i * 20, 300));
    }

    try {
        log("Attempt 1: arrayBuffer()");
        const buf = await rawFile.arrayBuffer();
        if (buf && buf.byteLength > 0) {
            const blob = new Blob([buf], { type: rawFile.type || guessedType });
            const hydrated = new File([blob], rawFile.name || `video_${Date.now()}.mp4`, { type: blob.type });
            log("Returning hydrated file (arrayBuffer)");
            return hydrated;
        } else {
            log("arrayBuffer returned 0 bytes — will try FileReader");
        }
    } catch (err) {
        log("arrayBuffer threw", String(err));
    }

    try {
        log("Attempt 2: FileReader.readAsArrayBuffer");
        const buf = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error || new Error("FileReader error"));
            reader.readAsArrayBuffer(rawFile);
        });
        if (buf && buf.byteLength > 0) {
            const blob = new Blob([buf], { type: rawFile.type || guessedType });
            const hydrated = new File([blob], rawFile.name || `video_${Date.now()}.mp4`, { type: blob.type });
            log("Returning hydrated file (FileReader)");
            return hydrated;
        } else {
            log("FileReader returned 0 bytes");
        }
    } catch (err) {
        log("FileReader threw", String(err));
    }

    try {
        log("Attempt 3: objectURL + fetch");
        const url = URL.createObjectURL(rawFile);
        const res = await fetch(url);
        const buf = await res.arrayBuffer();
        URL.revokeObjectURL(url);
        if (buf && buf.byteLength > 0) {
            const blob = new Blob([buf], { type: rawFile.type || guessedType });
            const hydrated = new File([blob], rawFile.name || `video_${Date.now()}.mp4`, { type: blob.type });
            log("Returning hydrated file (objectURL)");
            return hydrated;
        } else {
            log("objectURL+fetch returned 0");
        }
    } catch (err) {
        log("objectURL+fetch threw", String(err));
    }

    log("All hydration attempts failed — returning original (size=0)");
    return rawFile;
}

export default function VideoUploader({
    makeKey,
    onStart,
    onProgress,
    onCancel,
    onError,
    onComplete,
    canUpload = true,
    onRequireMeta,
    onClose,
    className = "",
    isRepValid = true,
}) {
    const [file, setFile] = useState(null);
    const [status, setStatus] = useState("idle"); // idle | uploading | paused | done | error | canceled
    const [progress, setProgress] = useState({ loaded: 0, total: 0, pct: 0, speed: 0, eta: null });
    const [error, setError] = useState("");
    const [s3Key, setS3Key] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const dragDepthRef = useRef(0);

    const taskRef = useRef(null);
    const startedAtRef = useRef(null);
    const { uiHint } = useMemo(getNetworkTuning, []);

    const [dbg, setDbg] = useState({
        origin: getOrigin(),
        ua: getUA(),
        uiHint,
        fileName: "",
        fileType: "",
        guessedType: "",
        usingContentType: false,
        partSizeMB: 5,
        queueSize: 2,
        progress: { loaded: 0, total: 0, pct: 0 },
        lastError: "",
        http: undefined,
        time: new Date().toISOString(),
        lines: [],
    });

    const log = (...args) => {
        console.log("[VideoUploader]", ...args);
        if (!DEBUG) return;
        setDbg((d) => ({
            ...d,
            lines: [
                ...d.lines,
                `[${new Date().toLocaleTimeString()}] ${args.map(a => (typeof a === "string" ? a : safeJSON(a))).join(" ")}`
            ].slice(-200),
        }));
    };

    const buildDebugText = () => {
        const net = (dbg.netCalls || [])
            .map(c =>
                `${c.t} ${c.kind} ${c.type || ""} ${c.method} ${c.origin}${c.path ? c.path : ""}` +
                `${c.status != null ? " status=" + c.status : ""}` +
                `${c.ready != null ? " ready=" + c.ready : ""}` +
                `${c.dt != null ? " " + c.dt + "ms" : ""}` +
                `${c.err ? " " + c.err : ""}`
            ).join("\n");

        return [
            "=== ENV ===",
            `origin: ${dbg.origin}`,
            `ua: ${dbg.ua}`,
            `uiHint: ${dbg.uiHint}`,
            "",
            "=== NET CALLS ===",
            net || "(none)",
            "",
            "=== LINES ===",
            (dbg.lines || []).join("\n") || "(none)",
        ].join("\n");
    };

    useEffect(() => {
        if (!DEBUG) return;

        const conn =
            (typeof navigator !== "undefined") &&
            (navigator.connection || navigator.webkitConnection || navigator.mozConnection);

        log(
            "ENV",
            { origin: getOrigin(), ua: getUA(), isMobile: isMobileUA() },
            { connection: conn && { effectiveType: conn.effectiveType, saveData: conn.saveData, rtt: conn.rtt, downlink: conn.downlink } }
        );

        (async () => {
            try {
                const s = await fetchAuthSession();
                log("AUTH session", {
                    identityId: s?.identityId ? "present" : "missing",
                    tokens: s?.tokens ? "present" : "missing"
                });
            } catch (e) {
                log("AUTH session ERROR", String(e));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!DEBUG || typeof window === "undefined") return;
        if (window.__vu_patched) return;
        window.__vu_patched = true;

        const calls = [];
        const keep = (x) => { calls.push(x); if (calls.length > 40) calls.shift(); setDbg(d => ({ ...d, netCalls: [...calls] })); };

        const origOpen = XMLHttpRequest.prototype.open;
        const origSend = XMLHttpRequest.prototype.send;

        XMLHttpRequest.prototype.open = function (method, url, ...rest) {
            this.__vu = { method, url };
            return origOpen.call(this, method, url, ...rest);
        };
        XMLHttpRequest.prototype.send = function (...args) {
            const started = Date.now();
            const info = this.__vu || {};
            const report = (type) => {
                try {
                    const u = new URL(info.url, window.location.href);
                    keep({
                        t: new Date().toLocaleTimeString(),
                        kind: "XHR",
                        type,
                        method: info.method,
                        origin: u.origin,
                        path: u.pathname + u.search,
                        status: this.status,
                        ready: this.readyState,
                        dt: Date.now() - started
                    });
                } catch { }
            };
            this.addEventListener("error", () => report("error"));
            this.addEventListener("abort", () => report("abort"));
            this.addEventListener("timeout", () => report("timeout"));
            this.addEventListener("loadend", () => report("end"));
            return origSend.apply(this, args);
        };

        const origFetch = window.fetch;
        window.fetch = async (...args) => {
            let method = "GET", urlStr;
            try {
                if (typeof args[0] === "string") { urlStr = args[0]; method = (args[1]?.method || "GET").toUpperCase(); }
                else { urlStr = args[0].url; method = (args[0].method || "GET").toUpperCase(); }
                const u = new URL(urlStr, window.location.href);
                keep({ t: new Date().toLocaleTimeString(), kind: "fetch", type: "start", method, origin: u.origin, path: u.pathname + u.search });
            } catch { }
            try {
                return await origFetch(...args);
            } catch (err) {
                try {
                    const u = new URL(urlStr, window.location.href);
                    keep({ t: new Date().toLocaleTimeString(), kind: "fetch", type: "error", method, origin: u.origin, path: u.pathname + u.search, err: String(err) });
                } catch { }
                throw err;
            }
        };
    }, []);

    const runMobileConnectivityTests = async () => {
        const tests = [
            { name: "S3 bucket root", url: `https://${"t4astorage580ed-dev"}.s3.${"us-east-2"}.amazonaws.com/` },
            { name: "S3 regional", url: `https://s3.${"us-east-2"}.amazonaws.com/` },
            { name: "Public JSON", url: "https://jsonplaceholder.typicode.com/todos/1" },
        ];

        for (const t of tests) {
            try {
                const res = await fetch(t.url, { method: "GET", mode: "cors" });
                log("TEST", t.name, t.url, "=>", `status ${res.status}`);
            } catch (e) {
                log("TEST", t.name, t.url, "=> ERROR", String(e));
            }
        }

        try {
            await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.open("GET", `https://${"t4astorage580ed-dev"}.s3.${"us-east-2"}.amazonaws.com/`);
                xhr.onload = () => resolve();
                xhr.onerror = () => reject(new Error("XHR error"));
                xhr.send();
            });
            log("TEST", "XHR to bucket root => OK (got onload)");
        } catch (e) {
            log("TEST", "XHR to bucket root => ERROR", String(e));
        }
    };

    useEffect(() => {
        setStatus("idle");
        setProgress({ loaded: 0, total: file?.size || 0, pct: 0, speed: 0, eta: null });
        setError("");
        taskRef.current = null;

        if (file) {
            const hasExt = /\.[a-z0-9]{2,5}$/i.test(file.name || "");
            const fallbackName = `video_${Date.now()}.mp4`;
            const baseName = (file.name && hasExt ? file.name : fallbackName)
                .replace(/\s+/g, "_")
                .replace(/[^a-zA-Z0-9._-]/g, "");

            setS3Key(makeKey?.(file) || `${DEFAULT_PREFIX}${baseName}`);
        } else {
            setS3Key(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    const handleFile = (f) => {
        if (!f) {
            setError("No video selected.");
            return;
        }

        setError("");

        const guessed = guessMimeFromName(f.name || "");
        const isVideoByType = !!f.type && f.type.startsWith("video/");
        const isVideoByGuess = !!guessed;

        if (!isVideoByType && !isVideoByGuess) {
            setError("");
        } else {
            setError("");
        }

        setFile(f);

        if (DEBUG) {
            setDbg((d) => ({
                ...d,
                fileName: f.name || "(unnamed)",
                fileType: f.type || "",
                guessedType: guessed || "",
            }));
            log("FILE", { name: f.name, size: f.size, type: f.type || "(empty)" }, { guessed });
        }
    };

    const startUpload = async () => {
        if (!file || !s3Key) return;
        if (!canUpload) { onRequireMeta?.(); return; }
        if (!isRepValid) { onRequireMeta?.(); return; }

        try {
            setStatus("uploading");
            setError("");
            startedAtRef.current = Date.now();
            onStart?.();

            const readyFile = await hydrateMobileCloudFile(file, log);

            if (!readyFile || readyFile.size === 0) {
                const msg = "This file couldn’t be hydrated (still 0 bytes). If it’s from cloud storage, save it locally and pick again.";
                log("[HYDRATE]", msg, { name: readyFile?.name, size: readyFile?.size, type: readyFile?.type });
                setStatus("error"); setError(msg); onError?.(msg); return;
            }

            const fallbackType = guessMimeFromName(readyFile.name) || "application/octet-stream";
            const contentType = readyFile.type || fallbackType;
            if (DEBUG) setDbg(d => ({ ...d, usingContentType: Boolean(contentType) }));

            const presignRes = await fetch(LAMBDA_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    key: s3Key,
                    contentType,
                    omitContentType: false,
                }),
            });

            if (!presignRes.ok) {
                const txt = await presignRes.text().catch(() => "");
                throw new Error(`Presign failed (${presignRes.status}). ${txt}`);
            }

            const { method, url, headers: putHeaders } = await presignRes.json();
            if (!url) throw new Error("Presign response missing URL");

            try {
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open(method || "PUT", url);

                    if (putHeaders && typeof putHeaders === "object") {
                        Object.entries(putHeaders).forEach(([k, v]) => {
                            if (v && k.toLowerCase() !== "host") xhr.setRequestHeader(k, v);
                        });
                    }
                    xhr.setRequestHeader("Content-Type", contentType);

                    xhr.timeout = 15 * 60 * 1000;

                    xhr.upload.onprogress = (e) => {
                        const total = (e.total && e.total > 0) ? e.total : (readyFile.size || 0);
                        const loaded = Math.min(e.loaded || 0, total || e.loaded || 0);
                        const now = Date.now();
                        const elapsedSec = Math.max(0.001, (now - startedAtRef.current) / 1000);
                        const speed = loaded / elapsedSec;
                        const remaining = total ? Math.max(0, total - loaded) : null;
                        const etaSec = speed > 0 && remaining != null ? remaining / speed : null;
                        const pct = total ? Math.floor((loaded / total) * 100) : (e.lengthComputable ? Math.floor((loaded / (e.total || 1)) * 100) : 0);
                        const next = { loaded, total: total || 0, pct, speed, eta: etaSec };
                        setProgress(next);
                        onProgress?.(next);
                    };

                    xhr.onerror = () => reject(new Error("Network error PUT to S3"));
                    xhr.ontimeout = () => reject(new Error("Upload timed out"));
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) resolve();
                        else reject(new Error(`S3 PUT failed (${xhr.status})`));
                    };

                    xhr.send(readyFile);
                    taskRef.current = { cancel: () => xhr.abort?.() };
                });

                const publicUrl = `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${s3Key}`;
                if (DEBUG) log("DONE", { key: s3Key, url: publicUrl });

                setStatus("done");
                const finalTotal = readyFile.size || progress.total || 0;
                setProgress({ loaded: finalTotal, total: finalTotal, pct: 100, speed: 0, eta: 0 });
                taskRef.current = null;

                onComplete?.({ key: s3Key, url: publicUrl, level: "public" });
                return;
            } catch (firstErr) {
                const looksLikePreflightBlock =
                    /Network error PUT to S3/i.test(firstErr?.message || "") ||
                    (firstErr?.message || "").includes("(0)");

                if (!looksLikePreflightBlock) throw firstErr;

                if (DEBUG) log("FALLBACK", "Retrying without Content-Type header");

                const alt = await fetch(LAMBDA_URL, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ key: s3Key, omitContentType: true }),
                });
                if (!alt.ok) {
                    const txt = await alt.text().catch(() => "");
                    throw new Error(`Presign (no-CT) failed (${alt.status}). ${txt}`);
                }
                const { method: m2, url: u2 } = await alt.json();

                const buf = await new Response(readyFile).arrayBuffer();
                const headerlessBlob = new Blob([buf], { type: "" });

                const put = await fetch(u2, { method: m2 || "PUT", body: headerlessBlob, mode: "cors" });
                if (!put.ok) throw new Error(`S3 PUT (no-CT) failed (${put.status})`);

                const publicUrl = `https://${S3_BUCKET}.s3.${S3_REGION}.amazonaws.com/${s3Key}`;
                setStatus("done");
                const finalTotal = readyFile.size || 0;
                setProgress({ loaded: finalTotal, total: finalTotal, pct: 100, speed: 0, eta: 0 });
                taskRef.current = null;
                onComplete?.({ key: s3Key, url: publicUrl, level: "public" });
                return;
            }

        } catch (e) {
            if (status === "canceled") return;
            const msg = e?.message || "Upload failed. Please try again.";
            if (DEBUG) {
                log("ERROR RAW", e);
                log("ERROR SUMMARY", { message: e?.message });
                setDbg((d) => ({ ...d, lastError: msg }));
            }
            setStatus("error");
            setError(msg);
            onError?.(msg);
        }
    };

    const cancelUpload = async () => {
        try {
            if (taskRef.current?.cancel) {
                setStatus("canceled");
                await taskRef.current.cancel();
            } else {
                setStatus("canceled");
            }
            onCancel?.();
            onClose?.();
        } catch { }
    };

    const disabledPicker = status === "uploading" || !isRepValid;
    const disabledStart = status === "uploading" || !file || !canUpload || !isRepValid;

    const fillClass =
        status === "error" ? styles.vuFillError :
            status === "canceled" ? styles.vuFillCancel :
                styles.vuFill;

    return (
        <div className={`${styles.vuRoot} ${className || ""}`}>
            {/* CHANGED: removed htmlFor to avoid implicit click; added onClick to use system file picker first,
                added hover yellow + pointer cursor, accessibility roles */}
            <label
                htmlFor="file"
                className={[
                    styles.vuDrop,
                    styles.vuDropPrimary,
                    disabledPicker ? styles.vuDropDisabled : "",
                    isDragActive ? styles.vuDropDragActive : "",
                ].join(" ")}
                aria-disabled={disabledPicker}
                title={!isRepValid ? "Enter a valid rep number to add a video" : undefined}
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabledPicker) return;
                    if (!isVideoDrag(e)) return;

                    dragDepthRef.current += 1;
                    setIsDragActive(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (disabledPicker) {
                        e.dataTransfer.dropEffect = "none";
                        setIsDragActive(false);
                        return;
                    }

                    if (!isVideoDrag(e)) {
                        e.dataTransfer.dropEffect = "none";
                        setIsDragActive(false);
                        return;
                    }

                    e.dataTransfer.dropEffect = "copy";
                    setIsDragActive(true);
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabledPicker) return;
                    if (!isVideoDrag(e)) return;

                    dragDepthRef.current -= 1;
                    if (dragDepthRef.current <= 0) {
                        dragDepthRef.current = 0;
                        setIsDragActive(false);
                    }
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    dragDepthRef.current = 0;
                    setIsDragActive(false);

                    if (disabledPicker) return;

                    const f = e.dataTransfer.files?.[0];
                    // Optional: ignore non-video drops
                    if (f && (f.type || "").startsWith("video/")) handleFile(f);
                }}
            >
                <input
                    id="file"
                    type="file"
                    accept="video/*"
                    className="hidden"
                    style={{
                        position: "absolute",
                        width: 1,
                        height: 1,
                        padding: 0,
                        margin: -1,
                        overflow: "hidden",
                        clip: "rect(0 0 0 0)",
                        whiteSpace: "nowrap",
                        border: 0,
                    }}
                    disabled={disabledPicker} // <-- already respects isRepValid via disabledPicker
                    onClick={(e) => { e.currentTarget.value = ""; }}
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className={styles.vuDropTitle}>
                    {file ? <strong>{file.name}</strong> : (
                        <>
                            <span className={styles.vuDropTitleDesktop}>
                                Click to add video OR drop video here
                            </span>
                            <span className={styles.vuDropTitleMobile}>Add video</span>
                        </>
                    )}
                </div>

                {!isRepValid && (
                    <div className={styles.vuStatusError} style={{ marginTop: 8 }}>
                        Enter a valid Rep # to enable uploads.
                    </div>
                )}

                {uiHint !== "normal" && (
                    <div className={styles.vuNetwork}>
                        Network: {uiHint} — uploads may take longer
                    </div>
                )}
                {error && (
                    <div className={styles.vuStatusError} style={{ marginTop: 8 }}>
                        {error}
                    </div>
                )}
            </label>

            <div className={styles.vuButtons}>
                <button
                    onClick={startUpload}
                    disabled={disabledStart}
                    className={styles.vuBtn}
                >
                    {status === "uploading" ? "Uploading…" : "Start upload"}
                </button>

                {/* REMOVED: "Pick from Files" button (now handled by clicking the drop zone) */}

                {/* REMOVED: Pause/Resume button */}

                <button
                    onClick={cancelUpload}
                    className={`${styles.vuBtn} ${styles.vuBtnDanger}`}
                    title="Cancel and close"
                >
                    Cancel
                </button>
            </div>

            {(status === "uploading" || status === "paused" || status === "done" || status === "error" || status === "canceled") && (
                <div className={styles.vuProgressWrap}>
                    <div className={styles.vuBar}>
                        <div className={fillClass} style={{ width: `${progress.pct}%` }} />
                    </div>

                    <div className={styles.vuStats}>
                        <span>{progress.pct}%</span>
                        <span>{fmt(progress.loaded)} / {fmt(progress.total)}</span>
                        {(status === "uploading" || status === "paused") && (
                            <>
                                <span>• {fmt(progress.speed)}/s</span>
                                <span>• ETA: {formatEta(progress.eta)}</span>
                            </>
                        )}
                    </div>

                    {status === "done" && <div className={styles.vuStatusDone}>Upload complete 🎉</div>}
                    {status === "paused" && <div className={styles.vuStatusPaused}>Upload paused</div>}
                    {status === "canceled" && <div className={styles.vuStatusCanceled}>Upload canceled.</div>}
                    {status === "error" && <div className={styles.vuStatusError}>{error}</div>}
                </div>
            )}

            {false && DEBUG && (
                <details style={{ marginTop: 12, fontSize: 12 }}>
                    <summary>Debug info</summary>
                    <div style={{ padding: 8, border: "1px dashed #ccc", borderRadius: 8, marginTop: 8, overflow: "auto", maxHeight: 260 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, justifyContent: "center", textAlign: "center" }}>
                            <button
                                type="button"
                                onClick={() => navigator.clipboard?.writeText(buildDebugText())}
                            >
                                Copy debug log
                            </button>
                            <button type="button" onClick={runMobileConnectivityTests}>
                                Run connectivity tests
                            </button>
                        </div>

                        <div><strong>Origin</strong>: {dbg.origin}</div>
                        <div><strong>UserAgent</strong>: {dbg.ua}</div>
                        <div><strong>uiHint</strong>: {dbg.uiHint}</div>
                        <div><strong>File</strong>: {dbg.fileName || "—"}</div>
                        <div><strong>file.type</strong>: {dbg.fileType || "∅"}</div>
                        <div><strong>guessed</strong>: {dbg.guessedType || "∅"}</div>
                        <div><strong>contentType used?</strong>: {String(dbg.usingContentType)}</div>
                        <div><strong>partSizeMB</strong>: {dbg.partSizeMB}</div>
                        <div><strong>queueSize</strong>: {dbg.queueSize}</div>
                        <div><strong>progress</strong>: {dbg.progress.pct}% ({fmt(dbg.progress.loaded)} / {fmt(dbg.progress.total)})</div>

                        <div><strong>target host</strong>: {(dbg.netCalls?.slice(-1)[0]?.origin) || "?"}</div>
                        <hr />
                        <pre style={{ whiteSpace: "pre-wrap" }}>
                            {(dbg.netCalls || []).map(c =>
                                `${c.t} ${c.kind} ${c.type || ""} ${c.method} ${c.origin}${c.path ? c.path : ""}${c.status != null ? " status=" + c.status : ""}${c.ready != null ? " ready=" + c.ready : ""}${c.dt != null ? " " + c.dt + "ms" : ""}${c.err ? " " + c.err : ""}`
                            ).join("\n")}
                        </pre>

                        {dbg.lastError && <div style={{ color: "#b00020" }}><strong>lastError</strong>: {dbg.lastError}</div>}
                        <hr />
                        <pre style={{ whiteSpace: "pre-wrap" }}>{dbg.lines.join("\n")}</pre>
                    </div>
                </details>
            )}
        </div>
    );
}
